// WhatsAppPreview.jsx
import React, { useState, useMemo, useEffect } from "react";
import { CheckCheck } from "lucide-react";
import WhatsAppMessageFormatter from "./WhatsAppMessageFormatter";

const WhatsAppPreview = React.memo(
  ({ messageType, message, caption, mediaUrl, phoneNumber, mediaFile }) => {
    // Memoize time string to prevent unnecessary recalculations
    const timeString = useMemo(() => {
      const now = new Date();
      return now.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    }, []); // Only compute once

    // Memoize phone number formatting
    const formattedPhoneNumber = useMemo(() => {
      const formatIndianPhoneNumber = (phone) => {
        if (!phone) return "+91 98765 43210";
        // Clean phone number
        const cleaned = phone.replace(/\D/g, "");

        // Format as Indian number: +91 XXXXX XXXXX
        if (cleaned.length >= 10) {
          if (cleaned.startsWith("91")) {
            // Already has country code
            const number = cleaned.slice(2);
            return `+91 ${number.slice(0, 5)} ${number.slice(5)}`;
          } else {
            // Add country code
            return `+91 ${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
          }
        }
        return phone;
      };
      return formatIndianPhoneNumber(phoneNumber);
    }, [phoneNumber]);

    // Memoize blob URL creation to prevent video flickering
    const mediaBlobUrl = useMemo(() => {
      if (mediaFile) {
        return URL.createObjectURL(mediaFile);
      }
      return null;
    }, [mediaFile]);

    // Clean up blob URL when component unmounts or mediaFile changes
    useEffect(() => {
      return () => {
        if (mediaBlobUrl) {
          URL.revokeObjectURL(mediaBlobUrl);
        }
      };
    }, [mediaBlobUrl]);

    // Memoize media preview rendering with stable blob URL
    const renderMediaPreview = useMemo(() => {
      if (messageType === "image") {
        return (
          <div className="mb-2 rounded-lg overflow-hidden">
            {mediaBlobUrl ? (
              <img
                src={mediaBlobUrl}
                alt="Preview"
                className="w-full max-h-64 object-cover rounded-lg"
              />
            ) : (
              <div className="w-full h-40 bg-gray-100 rounded-lg flex items-center justify-center">
                <span className="text-gray-600">📷 Image Preview</span>
              </div>
            )}
          </div>
        );
      }

      if (messageType === "video") {
        return (
          <div className="mb-2 rounded-lg overflow-hidden">
            {mediaBlobUrl ? (
              <video
                key={mediaBlobUrl} // Force new video element when URL changes
                src={mediaBlobUrl}
                className="w-full max-h-64 object-cover rounded-lg"
                controls
                muted
                playsInline
                preload="metadata"
              />
            ) : (
              <div className="w-full h-40 bg-gray-100 rounded-lg flex items-center justify-center">
                <span className="text-gray-600">🎥 Video Preview</span>
              </div>
            )}
          </div>
        );
      }

      if (messageType === "document") {
        return (
          <div className="mb-2 bg-gray-100 rounded-lg p-3 flex items-center space-x-3">
            <div className="w-10 h-10 bg-red-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-xs">PDF</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-gray-900 font-medium text-sm truncate">
                {mediaFile ? mediaFile.name : "Document.pdf"}
              </div>
              <div className="text-gray-600 text-xs">
                {mediaFile
                  ? `${Math.round(mediaFile.size / 1024)} KB`
                  : "123 KB"}{" "}
                - PDF
              </div>
            </div>
            <button className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
              <svg
                className="w-4 h-4 text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </button>
          </div>
        );
      }

      return null;
    }, [messageType, mediaBlobUrl, mediaFile]);

    // Memoize message text computation
    const messageText = useMemo(() => {
      if (messageType === "text") {
        return message || "Type your message here...";
      }
      return caption || "";
    }, [messageType, message, caption]);

    // Memoize text content check
    const hasTextContent = useMemo(() => {
      if (messageType === "text") {
        return message && message.trim();
      }
      return caption && caption.trim();
    }, [messageType, message, caption]);

    // Get current time for status bar
    const statusBarTime = useMemo(() => {
      const now = new Date();
      return now.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    }, []);

    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">WhatsApp Preview</h3>

        {/* Phone Frame Container */}
        <div className="flex justify-center items-start">
          {/* Phone Chassis */}
          <div className="relative bg-gray-900 rounded-[2.5rem] p-2 shadow-2xl">
            {/* Phone Screen */}
            <div className="bg-black rounded-[2rem] overflow-hidden w-[280px] h-[480px] relative">
              {/* Status Bar */}
              <div className="bg-white h-8 flex items-center justify-between px-4 text-xs font-medium text-gray-900">
                <span className="text-[10px]">{statusBarTime}</span>
                <div className="flex items-center space-x-1">
                  {/* Signal Bars */}
                  <div className="flex items-end space-x-0.5">
                    <div className="w-1 h-1.5 bg-gray-900 rounded-t"></div>
                    <div className="w-1 h-2 bg-gray-900 rounded-t"></div>
                    <div className="w-1 h-2.5 bg-gray-900 rounded-t"></div>
                    <div className="w-1 h-3 bg-gray-900 rounded-t"></div>
                  </div>
                  {/* WiFi Icon */}
                  <svg className="w-3.5 h-3.5 text-gray-900 ml-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M17.293 13.293A8 8 0 016.707 2.707a8 8 0 1010.586 10.586z" clipRule="evenodd" />
                    <path d="M9.663 17H7v-2h2.663l1-1H7V12h4.663l2-2H7V9h7.663l3-3H7V4h13v1l-3 3v1l-3 3v1l-2 2v1l-1 1H9.663z" />
                  </svg>
                  {/* Battery Icon */}
                  <div className="flex items-center space-x-0.5 ml-1">
                    <div className="w-5 h-2.5 border border-gray-900 rounded-sm">
                      <div className="w-3.5 h-2 bg-gray-900 rounded-sm m-0.5"></div>
                    </div>
                    <div className="w-0.5 h-1.5 bg-gray-900 rounded-r"></div>
                  </div>
                </div>
              </div>

              {/* WhatsApp Content Area */}
              <div className="bg-gray-50 h-[calc(100%-2rem)] flex flex-col overflow-hidden">
                {/* WhatsApp Header */}
                <div className="bg-[#075e54] px-3 py-2.5 flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gray-400 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-semibold text-xs">
                      {phoneNumber ? phoneNumber.charAt(phoneNumber.length - 1) : "0"}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-medium text-sm">
                      {formattedPhoneNumber}
                    </div>
                    <div className="text-white/80 text-xs">online</div>
                  </div>
                </div>

                {/* Chat Background */}
                <div className="flex-1 p-3 flex flex-col justify-end bg-[#ece5dd] relative overflow-hidden">
                  {/* WhatsApp Pattern Background */}
                  <div className="absolute inset-0 opacity-[0.03]" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h100v100H0z' fill='%23000'/%3E%3Cpath d='M0 0l50 50M100 0L50 50M0 100l50-50M100 100L50 50' stroke='%23000' stroke-width='0.5'/%3E%3C/svg%3E")`,
                    backgroundSize: '200px 200px'
                  }}></div>

                  {/* Sent Message Bubble */}
                  <div className="flex justify-end mb-3 relative z-10">
                    <div className="relative max-w-[75%] min-w-[80px]">
                      <div className="bg-[#005c4b] rounded-lg px-2.5 py-1.5 shadow-sm">
                        {/* Message Tail */}
                        <div className="absolute -right-1 bottom-0 w-0 h-0 border-l-[8px] border-l-[#005c4b] border-b-[8px] border-b-transparent"></div>

                        {/* Media Content */}
                        {renderMediaPreview}

                        {/* Text Content with WhatsApp Formatting */}
                        {hasTextContent && (
                          <WhatsAppMessageFormatter
                            text={messageText}
                            className="text-white text-[14px] leading-[1.4] break-words"
                          />
                        )}

                        {/* Message Footer */}
                        <div className="flex items-end justify-end space-x-1 mt-1 min-h-[14px]">
                          <span className="text-white/80 text-[11px] leading-none">
                            {timeString}
                          </span>
                          <CheckCheck className="w-3.5 h-3.5 text-blue-300 flex-shrink-0" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Phone Notch (optional - for modern phones) */}
            <div className="absolute top-2 left-1/2 transform -translate-x-1/2 w-24 h-5 bg-gray-900 rounded-b-2xl"></div>
          </div>
        </div>

        {/* Preview Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-center space-x-2">
            <div className="text-blue-600">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="text-blue-800 text-sm">
              <span className="font-medium">Preview:</span> This is how your
              message will appear in WhatsApp
            </div>
          </div>
        </div>
      </div>
    );
  }
);

// Add display name for debugging
WhatsAppPreview.displayName = "WhatsAppPreview";

export default WhatsAppPreview;
